/**
 * Location Management - 库位管理列表页
 * 简单列表 + FilterPanel + 详情 Drawer
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Input, Select, Button, Table, Space, Col, message, Image, Upload, Modal, Spin } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import { ExportOutlined, ImportOutlined, InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import FilterPanel from '../../../../components/common/FilterPanel';
import type { LocationDTO, QueryLocationPageRequest } from '../../../../types/store-resource/location';
import type { Paging } from '../../../../types/common';
import * as locationService from '../../../../services/store-resource/locationService';
import * as itemService from '../../../../services/master-data/itemService';

const LocationList: React.FC = () => {
  const { t } = useTranslation();
  const [searchForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<LocationDTO[]>([]);
  const [pagination, setPagination] = useState<Paging>({ currentPage: 1, pageSize: 10, totalCount: 0 });

  // Item remote search
  const [itemOptions, setItemOptions] = useState<{ label: string; value: string }[]>([]);
  const [itemSearching, setItemSearching] = useState(false);
  const itemSearchTimer = useRef<ReturnType<typeof setTimeout>>();

  const handleItemSearch = (keyword: string) => {
    if (itemSearchTimer.current) clearTimeout(itemSearchTimer.current);
    if (!keyword || keyword.length < 2) {
      setItemOptions([]);
      return;
    }
    itemSearchTimer.current = setTimeout(async () => {
      setItemSearching(true);
      try {
        const res = await itemService.pageItem({ currentPage: 1, pageSize: 20, code: keyword });
        if (res.success && res.data) {
          setItemOptions(
            res.data.tableData.map((item) => ({
              label: `${item.code} - ${item.name || ''}`,
              value: item.code,
            }))
          );
        }
      } finally {
        setItemSearching(false);
      }
    }, 300);
  };

  // Import modal state
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [importLoading, setImportLoading] = useState(false);

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept: '.xlsx,.xls',
    fileList,
    beforeUpload: (file) => {
      setFileList([file as unknown as UploadFile]);
      return false;
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  const handleImportCancel = () => {
    setImportModalVisible(false);
    setFileList([]);
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error('Please select a file first');
      return;
    }

    const file = fileList[0] as unknown as File;
    setImportLoading(true);

    try {
      message.loading('Uploading file...', 0);
      const response = await locationService.importLocationFile(file);

      if ((response as any).data?.success !== false) {
        message.destroy();
        message.success('Item location file import submitted successfully');
        setImportModalVisible(false);
        setFileList([]);
        loadData(buildQueryParams(1, pagination.pageSize));
      } else {
        message.destroy();
        message.error((response as any).data?.errorMessage || 'Import failed');
      }
    } catch (err: any) {
      message.destroy();
      console.error('Import error:', err);
      message.error(err.message || 'Import failed, please try again');
    } finally {
      setImportLoading(false);
    }
  };

  // 加载列表数据
  const loadData = useCallback(async (params: QueryLocationPageRequest) => {
    setLoading(true);
    try {
      const response = await locationService.pageLocation(params);
      if (response.success && response.data) {
        setDataSource(response.data.tableData);
        setPagination(response.data.paging);
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // 初始化
  useEffect(() => {
    loadData({ currentPage: 1, pageSize: 10 });
  }, []);

  // 构建查询参数
  const buildQueryParams = (page: number, pageSize: number): QueryLocationPageRequest => {
    const values = searchForm.getFieldsValue();
    return {
      currentPage: page,
      pageSize,
      warehouseCode: values.warehouseCode || undefined,
      ownerCode: values.ownerCode || undefined,
      itemCode: values.itemCode || undefined,
      aisleCode: values.aisleCode || undefined,
      locationCode: values.locationCode || undefined,
      locationType: values.locationType || undefined,
    };
  };

  // 搜索
  const handleSearch = () => {
    loadData(buildQueryParams(1, pagination.pageSize));
  };

  // 重置
  const handleReset = () => {
    searchForm.resetFields();
    loadData({ currentPage: 1, pageSize: pagination.pageSize });
  };

  // 分页变更
  const handlePageChange = (page: number, pageSize: number) => {
    loadData(buildQueryParams(page, pageSize));
  };

  // 导出
  const [exportLoading, setExportLoading] = useState(false);
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const values = searchForm.getFieldsValue();
      const response = await locationService.exportLocation({
        itemCode: values.itemCode || undefined,
        locationCode: values.locationCode || undefined,
      });
      if (response.success && response.data) {
        const link = document.createElement('a');
        link.href = response.data;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success('Export successful');
      } else {
        message.error(response.errorMessage || 'Export failed');
      }
    } catch {
      message.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  };


  // 格式化日期
  const formatDateTime = (timestamp: number | null) => {
    return timestamp ? dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss') : '-';
  };

  // 库位类型选项
  const locationTypeOptions = [
    { value: 'STORAGE', label: 'Storage' },
    { value: 'PICKING', label: 'Picking' },
    { value: 'STAGING', label: 'Staging' },
  ];

  // 表格列定义
  const columns = [
    {
      title: 'Warehouse Code',
      dataIndex: 'warehouseCode',
      key: 'warehouseCode',
      width: 150,
    },
    {
      title: 'Owner Code',
      dataIndex: 'ownerCode',
      key: 'ownerCode',
      width: 130,
    },
    {
      title: 'Item Code',
      dataIndex: 'itemCode',
      key: 'itemCode',
      width: 130,
    },
    {
      title: 'Item Name',
      dataIndex: 'itemName',
      key: 'itemName',
      width: 180,
    },
    {
      title: 'Image',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 80,
      render: (url: string | null) => url ? (
        <Image src={url} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk3KFb2AAAAABJRU5ErkJggg==" />
      ) : '-',
    },
    {
      title: 'Aisle Code',
      dataIndex: 'aisleCode',
      key: 'aisleCode',
      width: 120,
    },
    {
      title: 'Location Code',
      dataIndex: 'locationCode',
      key: 'locationCode',
      width: 180,
    },
    {
      title: 'Location Type',
      dataIndex: 'locationType',
      key: 'locationType',
      width: 130,
    },
    {
      title: 'Updated Time',
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      width: 180,
      sorter: true,
      render: formatDateTime,
    },
  ];

  return (
    <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* 筛选区域 */}
      <FilterPanel
        form={searchForm}
        onFilter={handleSearch}
        onReset={handleReset}
        loading={loading}
      >
        <Col span={6}>
          <Form.Item name="itemCode" label="Item">
            <Select
              showSearch
              allowClear
              placeholder="Search item code"
              filterOption={false}
              onSearch={handleItemSearch}
              notFoundContent={itemSearching ? <Spin size="small" /> : null}
              options={itemOptions}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="locationCode" label="Location Code">
            <Input placeholder="Enter location code" allowClear />
          </Form.Item>
        </Col>
      </FilterPanel>

      {/* 操作栏 */}
      <div style={{ marginTop: 16, marginBottom: 16, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <Button icon={<ExportOutlined />} loading={exportLoading} onClick={handleExport}>
          Export
        </Button>
      </div>

      {/* 表格 */}
      <Table
        rowKey="locationCode"
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        scroll={{ x: 1100, y: 'calc(100vh - 380px)' }}
        style={{ marginTop: 16 }}
        pagination={{
          current: pagination.currentPage,
          pageSize: pagination.pageSize,
          total: pagination.totalCount,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} items`,
          onChange: handlePageChange,
        }}
      />

      {/* Import Modal */}
      <Modal
        title="Import Item Location Data"
        open={importModalVisible}
        onOk={handleUpload}
        onCancel={handleImportCancel}
        confirmLoading={importLoading}
        width={600}
      >
        <div style={{ marginBottom: '16px' }}>
          <p>Upload an Excel file to import item-location relationship data via SFTP simulation.</p>
          <p style={{ color: '#666', fontSize: '12px' }}>
            Supported formats: .xlsx, .xls
          </p>
        </div>

        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag file to this area to upload</p>
          <p className="ant-upload-hint">Only single file upload is supported.</p>
        </Upload.Dragger>
      </Modal>
    </div>
  );
};

export default LocationList;
